// Kalender uues kujunduses: where a work order sits beside the ones it overlaps.
//
// Vuetify's own 'stack' mode places each order in the first free spot, so a
// technician's day jumps from left to right and back. Here every technician
// keeps the lane of their first order that day:
//   - an order goes to its technician's lane when that lane is free;
//   - otherwise to the first free lane no other technician needs back before
//     this order ends; only if there is none, to any free lane;
//   - the lanes are one grid for the whole day, so a lane is always at the
//     same place, and an order widens to the right over lanes nobody uses
//     while it runs.
// A technician moves out of their lane only when their own orders overlap.
//
// Passed to v-calendar as event-overlap-mode: (events, firstWeekday,
// overlapThreshold) => (day, dayEvents, timed, reset) => visuals.

const startOf = event => event.startTimestampIdentifier
const endOf = event => event.endTimestampIdentifier

// Whose lane an order belongs to: its technician, or one shared key for the
// order being drawn on empty time.
const owner = event => {
  const data = event.input && event.input.data
  return data && data.user ? 'u' + data.user.id : 'new'
}

export function laneLayout (dayEvents, dayStart) {
  const items = dayEvents
    .map(event => ({ event, s: Math.max(dayStart, startOf(event)), e: endOf(event), who: owner(event) }))
    .sort((a, b) => a.s - b.s || b.e - a.e)

  const ends = []
  const home = {}
  const lord = {}
  items.forEach(item => {
    const free = lane => (ends[lane] || 0) <= item.s
    // Another technician comes back to this lane before this order is over.
    const wanted = lane => lord[lane] != null && lord[lane] !== item.who &&
      items.some(other => other !== item && other.who === lord[lane] && other.s >= item.s && other.s < item.e)
    let lane = home[item.who] != null && free(home[item.who]) ? home[item.who] : -1
    if (lane < 0) {
      lane = 0
      while (!free(lane) || wanted(lane)) lane++
    }
    ends[lane] = item.e
    item.lane = lane
    if (home[item.who] == null) {
      home[item.who] = lane
      if (lord[lane] == null) lord[lane] = item.who
    }
  })

  const lanes = Math.max(1, ...items.map(item => item.lane + 1))
  return items.map(item => {
    const beside = items
      .filter(other => other !== item && other.s < item.e && item.s < other.e && other.lane > item.lane)
      .map(other => other.lane)
    const stop = beside.length ? Math.min(...beside) : lanes
    return {
      event: item.event,
      column: item.lane,
      columnCount: lanes,
      left: item.lane * 100 / lanes,
      width: (stop - item.lane) * 100 / lanes
    }
  })
}

const dayIdentifier = day => day.year * 100000000 + day.month * 1000000 + day.day * 10000

export default function stickyLanes () {
  return (day, dayEvents, timed) => {
    if (timed) {
      return laneLayout(dayEvents, dayIdentifier(day))
    }
    // The row above the hours (planner days): one under the other, in order.
    return dayEvents
      .map((event, index) => ({ event, index }))
      .sort((a, b) => startOf(a.event) - startOf(b.event) || endOf(b.event) - endOf(a.event) || a.index - b.index)
      .map(({ event }, column, all) => ({ event, column, columnCount: all.length, left: 0, width: 100 }))
  }
}
